import type {
  FundraiseStatus,
  InvestorContext,
  InvestorReadiness,
  KycStatus,
  LaunchpadOffering,
} from '../models/launchpad.types';

export interface InvestorReadinessInput {
  isAuthenticated: boolean;
  profileComplete: boolean;
  walletConnected: boolean;
  kycStatus: KycStatus;
  fundraiseStatus: FundraiseStatus;
  /** @deprecated ignored — Stellar trustline no longer required for EVM IDO */
  stellarWalletConnected?: boolean;
  trustlineEstablished?: boolean;
}

export function resolveInvestorReadiness(
  input: InvestorReadinessInput,
): InvestorReadiness {
  const walletConnected =
    input.walletConnected || !!input.stellarWalletConnected;

  if (!input.isAuthenticated) {
    return 'needs_login';
  }
  if (!input.profileComplete) {
    return 'needs_profile';
  }
  if (!walletConnected) {
    return 'needs_wallet';
  }
  if (input.kycStatus !== 'approved') {
    return 'needs_kyc';
  }
  if (input.fundraiseStatus === 'upcoming') {
    return 'fundraise_not_started';
  }
  if (input.fundraiseStatus !== 'live') {
    return 'fundraise_closed';
  }
  return 'ready';
}

export function buildInvestorContext(
  input: InvestorReadinessInput,
  walletAddress?: string,
): InvestorContext {
  return {
    readiness: resolveInvestorReadiness(input),
    walletAddress,
    stellarAddress: walletAddress,
    kycStatus: input.kycStatus,
    trustlineEstablished: true,
    profileComplete: input.profileComplete,
  };
}

export function canInvestInOffering(
  offering: LaunchpadOffering,
  context: InvestorContext,
  now: Date = new Date(),
): boolean {
  if (
    offering.listingDraft ||
    !offering.idoSale?.contractAddress ||
    context.readiness !== 'ready'
  ) {
    return false;
  }

  const sale = offering.idoSale;
  const start = Date.parse(sale.saleStart);
  const end = Date.parse(sale.saleEnd);
  const ts = now.getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return false;
  }
  // Sale window is the source of truth (not project fundraise dates alone).
  return ts >= start && ts < end;
}

export function investorReadinessMessage(readiness: InvestorReadiness): string {
  switch (readiness) {
    case 'needs_login':
      return 'Sign in to invest in this offering.';
    case 'needs_profile':
      return 'Complete your profile before investing.';
    case 'needs_wallet':
      return 'Connect your crypto wallet to continue.';
    case 'needs_trustline':
      return 'Connect your crypto wallet to continue.';
    case 'needs_kyc':
      return 'Complete KYC verification before investing.';
    case 'fundraise_not_started':
      return 'This fundraise has not started yet.';
    case 'fundraise_closed':
      return 'This fundraise is no longer accepting investments.';
    case 'ready':
      return 'You are ready to invest.';
    default:
      return '';
  }
}
